import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { faCube, faGears, faUserGroup } from '@fortawesome/pro-light-svg-icons';
import {
  faCube as faCubeSolid,
  faGears as faGearsSolid,
  faUserGroup as faUserGroupSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IParsedOrganizationUserRoles } from '~/helpers/parseOrganizationUserRoles';
import type { INavigationRailSectionItem } from './NavigationRailSection';
import { parseOrganizationUserRoles } from '~/helpers/parseOrganizationUserRoles';
import { useOrganizationUserQuery } from '~/lib/api/modules/Organizations/hooks/useOrganizationUserQuery';
import { NavigationRailSection } from './NavigationRailSection';

export type IOrganizationNavigationRailContentProps = IContainerProps & {
  organizationId: string;
};

type IGetItemsProps = {
  baseUrl: string;
  t: TFunction<'translation', undefined>;
  roles?: IParsedOrganizationUserRoles;
};

const getItems = ({
  baseUrl,
  t,
  roles,
}: IGetItemsProps): Array<INavigationRailSectionItem> => [
  {
    label: t('OrganizationNavigationRailContent.applications'),
    faIcon: faCube,
    faActiveIcon: faCubeSolid,
    href: `${baseUrl}/apps`,
  },
  {
    label: t('OrganizationNavigationRailContent.organizationUsers'),
    faIcon: faUserGroup,
    faActiveIcon: faUserGroupSolid,
    href: `${baseUrl}/users`,
    matchPatterns: [`${baseUrl}/users`, `${baseUrl}/invitations`],
  },
  ...(roles?.atLeastAdmin
    ? [
        {
          label: t('OrganizationNavigationRailContent.settings'),
          faIcon: faGears,
          faActiveIcon: faGearsSolid,
          href: `${baseUrl}/settings`,
        },
      ]
    : []),
];

export const OrganizationNavigationRailContent: React.FC<
  IOrganizationNavigationRailContentProps
> = (props) => {
  const { organizationId } = props;
  const { t } = useTranslation();
  const organizationUserQuery = useOrganizationUserQuery({ organizationId });
  const organizationUserRoles = organizationUserQuery.data?.roles
    ? parseOrganizationUserRoles(organizationUserQuery.data?.roles)
    : undefined;

  const baseUrl = `/organizations/${organizationId}`;
  const items = useMemo(
    () => getItems({ baseUrl, t, roles: organizationUserRoles }),
    [baseUrl, t, organizationUserRoles],
  );

  return <NavigationRailSection items={items} />;
};
